import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Snackbar,
    Alert,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Toolbar,
    Typography,
    Chip, MenuItem, TablePagination, InputAdornment,
} from "@mui/material";
import {
    createPrompt, deletePromptById,
    getAllByTableNameAndRowId,
    getAllConfigs, getAllLlmTools,
    getAllPrompts, updateLlmToolDefinition, updateLlmToolParameter,
    updatePrompt,
    upsertConfig
} from "../api/AiAgentApi";
import AuditLogFeed from "./AuditLogFeed";
import {useNavigate} from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Edit as EditIcon,
    Refresh as RefreshIcon,
    Save as SaveIcon,
    Search as SearchIcon,
    Clear as ClearIcon, ListAlt,
} from "@mui/icons-material";

/* ================= utils ================= */
const DEFAULT_GROUP_NAME = "Базовые настройки";

function isSecretKey(key) {
    const k = String(key || "").toLowerCase();
    return (
        k.includes("token") ||
        k.includes("secret") ||
        k.includes("password") ||
        k.includes("api_key") ||
        k.includes("apikey")
    );
}

function obfuscate(value) {
    if (value == null) return "";
    const s = String(value);
    if (s.length <= 6) return "••••••";
    return s.slice(0, 3) + "••••••" + s.slice(-3);
}

/* ================= component ================= */
function ConfigsSection(){
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);

    const [selectedGroup, setSelectedGroup] = useState(null);
    const [search, setSearch] = useState("");
    const [revealSecrets, setRevealSecrets] = useState(false);

    const [page, setPage] = useState(0);
    const rowsPerPage = 100; // фиксировано

    /* =============== load =============== */

    const load = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const data = await getAllConfigs();
            const list = data || [];
            setItems(list);

            const groupNames = Array.from(
                new Set(list.map((x) => x?.group?.name).filter(Boolean))
            );

            if (!groupNames.length) {
                setSelectedGroup(null);
                return;
            }

            // 👉 приоритетно выбираем "Базовые настройки"
            if (!selectedGroup) {
                if (groupNames.includes(DEFAULT_GROUP_NAME)) {
                    setSelectedGroup(DEFAULT_GROUP_NAME);
                } else {
                    setSelectedGroup(groupNames[0]);
                }
                return;
            }

            // если текущая группа исчезла
            if (!groupNames.includes(selectedGroup)) {
                if (groupNames.includes(DEFAULT_GROUP_NAME)) {
                    setSelectedGroup(DEFAULT_GROUP_NAME);
                } else {
                    setSelectedGroup(groupNames[0]);
                }
            }
        } catch (e) {
            setErr(
                e?.response?.data?.message || e.message || "Ошибка загрузки конфигов"
            );
        } finally {
            setLoading(false);
        }
    }, [selectedGroup]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!editing) return;
        setLogs([]);
        setLogsLoading(true);
        getAllByTableNameAndRowId("app_config", editing.key)
            .then((data) => setLogs(data || []))
            .catch(() => setLogs([]))
            .finally(() => setLogsLoading(false));
    }, [editing]);

    /* =============== derived =============== */

    const groups = useMemo(() => {
        const map = new Map();
        for (const it of items) {
            const g = it?.group;
            if (!g?.name) continue;
            if (!map.has(g.name)) {
                map.set(g.name, { name: g.name, description: g.description || "" });
            }
        }
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [items]);

    const selectedGroupInfo = useMemo(() => {
        if (!selectedGroup) return null;
        return groups.find((g) => g.name === selectedGroup) || null;
    }, [groups, selectedGroup]);

    const filteredItems = useMemo(() => {
        if (!selectedGroup) return [];
        const q = search.trim().toLowerCase();

        let list = items.filter((x) => x?.group?.name === selectedGroup);

        if (q) {
            list = list.filter((x) => {
                return (
                    String(x.key).toLowerCase().includes(q) ||
                    String(x.value ?? "").toLowerCase().includes(q) ||
                    String(x.description ?? "").toLowerCase().includes(q)
                );
            });
        }
        return list;
    }, [items, selectedGroup, search]);

    const pagedItems = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredItems.slice(start, start + rowsPerPage);
    }, [filteredItems, page]);

    useEffect(() => {
        setPage(0);
    }, [selectedGroup, search]);

    /* =============== actions =============== */

    const handleSave = async () => {
        if (!editing) return;
        setSaving(true);
        try {
            await upsertConfig(editing.key, {
                value: editing.value ?? "",
                description: editing.description ?? "",
            });
            setToast({
                open: true,
                message: "Конфиг сохранён",
                severity: "success",
            });
            setEditing(null);
            await load();
        } catch (e) {
            setToast({
                open: true,
                message: e?.message || "Ошибка сохранения",
                severity: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    /* =============== render =============== */

    return (
        <Paper elevation={2} sx={{ p: 2 }}>
            <Toolbar disableGutters sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Конфигурация
                </Typography>

                <IconButton onClick={load} disabled={loading}>
                    <RefreshIcon />
                </IconButton>
            </Toolbar>

            {/* filters */}
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField
                    select
                    size="small"
                    label="Группа"
                    value={selectedGroup || ""}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    sx={{ minWidth: 320 }}
                    helperText={selectedGroupInfo?.description || ""}
                >
                    {groups.map((g) => (
                        <MenuItem key={g.name} value={g.name}>
                            <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                                <Typography variant="body2">{g.name}</Typography>
                            </Box>
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    size="small"
                    label="Поиск"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: search && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearch("")}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {loading ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                    <CircularProgress />
                </Box>
            ) : err ? (
                <Alert severity="error">{err}</Alert>
            ) : (
                <>
                    <TableContainer sx={{ maxHeight: 640 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell width={80} align="right">
                                        ✏️
                                    </TableCell>
                                    <TableCell>Ключ</TableCell>
                                    <TableCell>Значение</TableCell>
                                    <TableCell>Описание</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pagedItems.map((row) => (
                                    <TableRow key={row.key} hover>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => setEditing({ ...row })}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>

                                        <TableCell sx={{ fontFamily: "monospace" }}>{row.key}</TableCell>

                                        <TableCell
                                            sx={{
                                                maxWidth: 360,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                fontFamily: isSecretKey(row.key) ? "monospace" : "inherit",
                                            }}
                                            title={String(row.value ?? "")}
                                        >
                                            {!revealSecrets && isSecretKey(row.key)
                                                ? obfuscate(row.value)
                                                : String(row.value ?? "")}
                                        </TableCell>

                                        <TableCell
                                            sx={{
                                                maxWidth: 420,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                            title={row.description || ""}
                                        >
                                            {row.description || ""}
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {pagedItems.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                            Нет данных
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[]}
                        count={filteredItems.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        labelRowsPerPage=""
                    />
                </>
            )}

            {/* dialog */}
            <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth>
                <DialogTitle>Редактирование</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Ключ"
                            value={editing?.key || ""}
                            InputProps={{ readOnly: true }}
                        />
                        <TextField
                            label="Значение"
                            value={editing?.value ?? ""}
                            onChange={(e) => setEditing((p) => ({ ...p, value: e.target.value }))}
                            multiline
                            minRows={2}
                        />
                        <TextField
                            label="Описание"
                            value={editing?.description ?? ""}
                            onChange={(e) =>
                                setEditing((p) => ({ ...p, description: e.target.value }))
                            }
                            multiline
                            minRows={2}
                        />
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6" sx={{ mb: 1 }}>
                        История изменений
                    </Typography>
                    {logsLoading ? <CircularProgress /> : <AuditLogFeed logs={logs} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditing(null)}>Отмена</Button>
                    <Button
                        startIcon={<SaveIcon />}
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        Сохранить
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
            >
                <Alert severity={toast.severity}>{toast.message}</Alert>
            </Snackbar>
        </Paper>
    );
}

// ---- Prompts section ----
function PromptsSection() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [editing, setEditing] = useState(null); // {id|null, ...fields}
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({open: false, message: "", severity: "success"});
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);

    // NEW: состояние для подтверждения удаления
    const [pendingDelete, setPendingDelete] = useState(null); // row или null
    const [deleting, setDeleting] = useState(false);

    const PURPOSE_CREATE = ["CHAT_BOT", "REALTIME"];
    const PURPOSE_ALL = ["CHAT_BOT", "REALTIME", "INTERNAL"];

    const load = async () => {
        setLoading(true);
        setErr("");
        try {
            const data = await getAllPrompts();
            const list = Array.isArray(data) ? data : [];
            setItems(
                list.filter(
                    p =>
                        p.purpose !== "CHAT_BOT" &&
                        ![1, 9, 10, 19, 21, 71, 43, 30].includes(p.id)
                )
            );
        } catch (e) {
            setErr(e?.response?.data?.message || e.message || "Ошибка загрузки промптов");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        if (editing?.id) {
            setLogsLoading(true);
            getAllByTableNameAndRowId("prompts", editing.id)
                .then(data => {
                    setLogs(data);
                    setLogsLoading(false);
                })
                .catch(() => setLogsLoading(false));
        } else {
            setLogs([]);
        }
    }, [editing]);

    const toolsToString = (tools) => {
        if (!tools) return "";
        if (Array.isArray(tools)) return tools.join(", ");
        try {
            const parsed = typeof tools === "string" ? JSON.parse(tools) : tools;
            if (Array.isArray(parsed)) return parsed.join(", ");
            return String(tools);
        } catch {
            return String(tools);
        }
    };

    const parseToolsInput = (input) => {
        if (!input) return [];
        return input.split(",").map(s => s.trim()).filter(Boolean);
    };

    const handleOpenCreate = () => {
        setEditing({
            id: null,
            type: "",
            text: "",
            description: "",
            purpose: "CHAT_BOT",
            temperature: "",
            maxTokens: "",
            toolsInput: "",
        });
        setLogs([]);
    };

    const handleSave = async () => {
        if (!editing) return;
        const isCreate = editing.id == null;

        if (isCreate && editing.purpose === "INTERNAL") {
            setToast({open: true, message: "При создании нельзя выбрать INTERNAL", severity: "error"});
            return;
        }

        setSaving(true);
        try {
            const payload = {
                text: editing.text ?? "",
                type: editing.type ?? "",
                description: editing.description ?? "",
                purpose: editing.purpose ?? null,
                temperature: editing.temperature !== "" && editing.temperature != null ? Number(editing.temperature) : null,
                maxTokens: editing.maxTokens !== "" && editing.maxTokens != null ? Number(editing.maxTokens) : null,
                tools: parseToolsInput(editing.toolsInput),
            };

            if (isCreate) {
                await createPrompt(payload);
                setToast({open: true, message: "Промпт добавлен", severity: "success"});
            } else {
                await updatePrompt(editing.id, payload);
                setToast({open: true, message: "Промпт сохранён", severity: "success"});
            }
            setEditing(null);
            await load();
        } catch (e) {
            setToast({open: true, message: e?.response?.data?.message || e.message, severity: "error"});
        } finally {
            setSaving(false);
        }
    };

    // NEW: подтверждение удаления
    const handleConfirmDelete = async () => {
        if (!pendingDelete) return;
        if (pendingDelete.purpose === "INTERNAL") {
            setToast({open: true, message: "Удаление недоступно для INTERNAL", severity: "error"});
            setPendingDelete(null);
            return;
        }
        setDeleting(true);
        try {
            await deletePromptById(pendingDelete.id);
            setToast({open: true, message: `Промпт #${pendingDelete.id} удалён`, severity: "success"});
            setPendingDelete(null);
            await load();
        } catch (e) {
            setToast({open: true, message: e?.response?.data?.message || e.message, severity: "error"});
        } finally {
            setDeleting(false);
        }
    };

    const groupedByPurpose = useMemo(() => {
        const map = items.reduce((acc, it) => {
            const key = it.purpose ?? "Без категории";
            if (!acc[key]) acc[key] = [];
            acc[key].push(it);
            return acc;
        }, {});
        Object.values(map).forEach(arr => arr.sort((a, b) => a.id - b.id));
        return map;
    }, [items]);

    return (
        <Paper elevation={2} sx={{p: 2}}>
            <Toolbar disableGutters sx={{mb: 1}}>
                <Typography variant="h6" sx={{flexGrow: 1}}>Промпты</Typography>
                <IconButton onClick={load} title="Обновить" disabled={loading} sx={{mr: 1}}>
                    <RefreshIcon/>
                </IconButton>
                <Button variant="contained" startIcon={<AddIcon/>} onClick={handleOpenCreate}>
                    Добавить
                </Button>
            </Toolbar>
            <Divider sx={{mb: 2}}/>

            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", p: 3}}>
                    <CircularProgress/>
                </Box>
            ) : err ? (
                <Alert severity="error">{err}</Alert>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="right">Действия</TableCell>
                                <TableCell>ID</TableCell>
                                <TableCell>Тип</TableCell>
                                <TableCell>Текст</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>maxTokens</TableCell>
                                <TableCell>temperature</TableCell>
                                <TableCell>tools</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.entries(groupedByPurpose)
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([purpose, rows]) => (
                                    <React.Fragment key={`grp-${purpose}`}>
                                        <TableRow>
                                            <TableCell
                                                colSpan={7}
                                                sx={{
                                                    bgcolor: "action.hover",
                                                    fontWeight: 600,
                                                    fontSize: "0.95rem"
                                                }}
                                            >
                                                {purpose}
                                            </TableCell>
                                        </TableRow>

                                        {rows.map((row) => {
                                            const canDelete = row.purpose !== "INTERNAL";
                                            return (
                                                <TableRow key={row.id} hover>
                                                    <TableCell align="right">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                setEditing({
                                                                    ...row,
                                                                    toolsInput: toolsToString(row.tools),
                                                                })
                                                            }
                                                            sx={{mr: 0.5}}
                                                            title="Редактировать"
                                                        >
                                                            <EditIcon fontSize="small"/>
                                                        </IconButton>

                                                        <span
                                                            title={canDelete ? "Удалить" : "Удаление недоступно для INTERNAL"}>
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={!canDelete || deleting}
                                                                    onClick={() => setPendingDelete(row)}
                                                                >
                                                                    <DeleteIcon fontSize="small"
                                                                                color={canDelete ? "error" : "disabled"}/>
                                                                </IconButton>
                                                            </span>
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>{row.id}</TableCell>
                                                    <TableCell><Chip size="small" label={row.type}/></TableCell>
                                                    <TableCell sx={{
                                                        maxWidth: 400,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis"
                                                    }}>
                                                        {row.text}
                                                    </TableCell>
                                                    <TableCell><Chip size="small" label={row.description}/></TableCell>
                                                    <TableCell>{row.maxTokens ?? ""}</TableCell>
                                                    <TableCell>{row.temperature ?? ""}</TableCell>
                                                    <TableCell sx={{
                                                        maxWidth: 240,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis"
                                                    }}>
                                                        {toolsToString(row.tools)}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </React.Fragment>
                                ))}

                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{py: 4, color: "text.secondary"}}>
                                        Нет данных
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Диалог создания/редактирования */}
            <Dialog
                open={!!editing}
                onClose={() => {
                    setLogs([]);
                    setEditing(null);
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>{editing?.id ? "Редактировать промпт" : "Добавить промпт"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="ID"
                                value={editing?.id ?? ""}
                                InputProps={{readOnly: true}}
                                sx={{width: 160}}
                            />
                            <TextField
                                label="Тип (enum)"
                                value={editing?.type ?? ""}
                                onChange={(e) => setEditing((prev) => ({...prev, type: e.target.value}))}
                                fullWidth
                                helperText="PromptEntity.Type"
                            />
                            <TextField
                                label="purpose (enum)"
                                value={editing?.purpose ?? ""}
                                onChange={(e) => setEditing((prev) => ({...prev, purpose: e.target.value}))}
                                sx={{width: 220}}
                                select
                                helperText={editing?.id ? "PromptEntity.Purpose (вкл. INTERNAL)" : "PromptEntity.Purpose"}
                            >
                                {(editing?.id ? PURPOSE_ALL : PURPOSE_CREATE).map(opt => (
                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                ))}
                            </TextField>
                        </Stack>

                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="maxTokens"
                                type="number"
                                value={editing?.maxTokens ?? ""}
                                onChange={(e) => setEditing((prev) => ({...prev, maxTokens: e.target.value}))}
                                sx={{width: 160}}
                            />
                            <TextField
                                label="temperature"
                                type="number"
                                inputProps={{step: "0.1", min: "0", max: "2"}}
                                value={editing?.temperature ?? ""}
                                onChange={(e) => setEditing((prev) => ({...prev, temperature: e.target.value}))}
                                sx={{width: 180}}
                            />
                            <TextField
                                label="tools (через запятую)"
                                value={editing?.toolsInput ?? ""}
                                onChange={(e) => setEditing((prev) => ({...prev, toolsInput: e.target.value}))}
                                placeholder="DateTools, TOOL_B"
                                fullWidth
                            />
                        </Stack>

                        <TextField
                            label="Описание"
                            value={editing?.description ?? ""}
                            onChange={(e) => setEditing((prev) => ({...prev, description: e.target.value}))}
                            fullWidth
                            multiline
                            minRows={2}
                        />

                        <TextField
                            label="Текст промпта"
                            value={editing?.text ?? ""}
                            onChange={(e) => setEditing((prev) => ({...prev, text: e.target.value}))}
                            fullWidth
                            multiline
                            minRows={6}
                        />

                        {editing?.id && (
                            <Box sx={{p: 2}}>
                                <Typography variant="h6" sx={{mb: 1.5}}>
                                    История изменений
                                </Typography>
                                {logsLoading && logs.length === 0 ? (
                                    <CircularProgress/>
                                ) : (
                                    <AuditLogFeed logs={logs}/>
                                )}
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setLogs([]);
                        setEditing(null);
                    }}>Отмена</Button>
                    <Button
                        startIcon={<SaveIcon/>}
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {editing?.id ? "Сохранить" : "Создать"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* NEW: диалог подтверждения удаления */}
            <Dialog
                open={!!pendingDelete}
                onClose={() => !deleting && setPendingDelete(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Удалить промпт?</DialogTitle>
                <DialogContent>
                    <Typography sx={{mb: 1.5}}>
                        Вы действительно хотите удалить промпт #{pendingDelete?.id}?
                    </Typography>
                    {pendingDelete?.purpose === "INTERNAL" && (
                        <Alert severity="warning">
                            Удаление недоступно для INTERNAL.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingDelete(null)} disabled={deleting}>Отмена</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleConfirmDelete}
                        disabled={deleting || pendingDelete?.purpose === "INTERNAL"}
                        startIcon={<DeleteIcon/>}
                    >
                        Удалить
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast((t) => ({...t, open: false}))}
            >
                <Alert severity={toast.severity} onClose={() => setToast((t) => ({...t, open: false}))}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
}

// ---- LLM Tools section ----
function LlmToolsSection() {
    const [items, setItems] = useState([]); // [{id, name, description, parameters: [...]}, ...]
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [toast, setToast] = useState({open: false, message: "", severity: "success"});

    const [editingTool, setEditingTool] = useState(null);         // { id, name, description }
    const [editingParam, setEditingParam] = useState(null);       // { id, name, paramName, description, required }
    const [savingTool, setSavingTool] = useState(false);
    const [savingParam, setSavingParam] = useState(false);

    const load = async () => {
        setLoading(true);
        setErr("");
        try {
            const data = await getAllLlmTools();
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            setErr(e?.response?.data?.message || e.message || "Ошибка загрузки LLM-тулзов");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleSaveTool = async () => {
        if (!editingTool) return;
        setSavingTool(true);
        try {
            await updateLlmToolDefinition(editingTool.id, {
                description: editingTool.description ?? ""
            });
            setToast({open: true, message: "Описание метода сохранено", severity: "success"});
            setEditingTool(null);
            await load();
        } catch (e) {
            setToast({
                open: true,
                message: e?.response?.data?.message || e.message || "Ошибка сохранения метода",
                severity: "error"
            });
        } finally {
            setSavingTool(false);
        }
    };

    const handleSaveParam = async () => {
        if (!editingParam) return;
        setSavingParam(true);
        try {
            await updateLlmToolParameter(editingParam.id, {
                description: editingParam.description ?? "",
                required: editingParam.required,
            });
            setToast({open: true, message: "Параметр сохранён", severity: "success"});
            setEditingParam(null);
            await load();
        } catch (e) {
            setToast({
                open: true,
                message: e?.response?.data?.message || e.message || "Ошибка сохранения параметра",
                severity: "error"
            });
        } finally {
            setSavingParam(false);
        }
    };

    return (
        <Paper elevation={2} sx={{p: 2}}>
            <Toolbar disableGutters sx={{mb: 1}}>
                <Typography variant="h6" sx={{flexGrow: 1}}>
                    LLM Tools
                </Typography>
                <IconButton onClick={load} title="Обновить" disabled={loading}>
                    <RefreshIcon/>
                </IconButton>
            </Toolbar>
            <Divider sx={{mb: 2}}/>

            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", p: 3}}>
                    <CircularProgress/>
                </Box>
            ) : err ? (
                <Alert severity="error">{err}</Alert>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="right">Действия</TableCell>
                                <TableCell>Метод</TableCell>
                                <TableCell>Описание</TableCell>
                                <TableCell>Параметры</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map(tool => (
                                <TableRow key={tool.id} hover>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                setEditingTool({
                                                    id: tool.id,
                                                    name: tool.name,
                                                    description: tool.description || ""
                                                })
                                            }
                                            title="Редактировать метод"
                                        >
                                            <EditIcon fontSize="small"/>
                                        </IconButton>
                                    </TableCell>
                                    <TableCell sx={{whiteSpace: "nowrap"}}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {tool.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{
                                        maxWidth: 400,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                    }}>
                                        {tool.description}
                                    </TableCell>
                                    <TableCell sx={{maxWidth: 480}}>
                                        {(!tool.parameters || tool.parameters.length === 0) ? (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Нет параметров
                                            </Typography>
                                        ) : (
                                            <Stack spacing={0.5}>
                                                {tool.parameters.map(p => (
                                                    <Box
                                                        key={p.id}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                            borderRadius: 1,
                                                            border: theme => `1px solid ${theme.palette.divider}`,
                                                            px: 1,
                                                            py: 0.5,
                                                        }}
                                                    >
                                                        <Box sx={{mr: 1, minWidth: 140}}>
                                                            <Typography variant="body2" fontWeight={600}>
                                                                {p.paramName}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    display: "block",
                                                                    maxWidth: 260,
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis"
                                                                }}
                                                            >
                                                                {p.description}
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                                            <Chip
                                                                size="small"
                                                                label={p.required ? "required" : "optional"}
                                                                color={p.required ? "error" : "default"}
                                                            />
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    setEditingParam({
                                                                        ...p
                                                                    })
                                                                }
                                                                title="Редактировать параметр"
                                                            >
                                                                <EditIcon fontSize="small"/>
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}

                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{py: 4, color: "text.secondary"}}>
                                        Нет данных
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Диалог редактирования метода */}
            <Dialog
                open={!!editingTool}
                onClose={() => setEditingTool(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Редактировать метод</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            label="Ключ метода"
                            value={editingTool?.name || ""}
                            InputProps={{readOnly: true}}
                        />
                        <TextField
                            label="Описание"
                            value={editingTool?.description ?? ""}
                            onChange={(e) =>
                                setEditingTool(prev => ({...prev, description: e.target.value}))
                            }
                            fullWidth
                            multiline
                            minRows={3}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditingTool(null)}>Отмена</Button>
                    <Button
                        startIcon={<SaveIcon/>}
                        variant="contained"
                        onClick={handleSaveTool}
                        disabled={savingTool}
                    >
                        Сохранить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог редактирования параметра */}
            <Dialog
                open={!!editingParam}
                onClose={() => setEditingParam(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Редактировать параметр</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            label="Ключ параметра"
                            value={editingParam?.name || ""}
                            InputProps={{readOnly: true}}
                        />
                        <TextField
                            label="Имя параметра (Java)"
                            value={editingParam?.paramName || ""}
                            InputProps={{readOnly: true}}
                        />
                        <TextField
                            label="Описание"
                            value={editingParam?.description ?? ""}
                            onChange={(e) =>
                                setEditingParam(prev => ({...prev, description: e.target.value}))
                            }
                            fullWidth
                            multiline
                            minRows={3}
                        />
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Typography variant="body2">Обязательный параметр</Typography>
                            <Button
                                variant={editingParam?.required ? "contained" : "outlined"}
                                size="small"
                                onClick={() =>
                                    setEditingParam(prev => ({...prev, required: !prev?.required}))
                                }
                            >
                                {editingParam?.required ? "Да" : "Нет"}
                            </Button>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditingParam(null)}>Отмена</Button>
                    <Button
                        startIcon={<SaveIcon/>}
                        variant="contained"
                        onClick={handleSaveParam}
                        disabled={savingParam}
                    >
                        Сохранить
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast(t => ({...t, open: false}))}
            >
                <Alert
                    severity={toast.severity}
                    onClose={() => setToast(t => ({...t, open: false}))}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
}

export const HomePage = () => {
    const navigate = useNavigate();

    return (
        <Container maxWidth="lg" sx={{py: 3}}>
            <Stack spacing={3}>
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<ListAlt/>}
                    onClick={() => navigate("/audit-logs")}
                    sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                        boxShadow: 1,
                        "&:hover": {boxShadow: 3},
                    }}
                >
                    Логи изменений
                </Button>

                <ConfigsSection/>
                <PromptsSection/>
                <LlmToolsSection/>
            </Stack>
        </Container>
    );
};